// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as taskActions from 'admin/task/actions';
import { PageHeader } from 'admin/common/components/PageHeader/PageHeader';
import {
    Filters,
    IFilterOptions,
    ISelectOption,
} from 'admin/task/components/Filters';
import { PropsValue } from 'react-select';
import TaskTable from 'admin/task/containers/TaskTable';
import { computeResources } from 'common/api/resources/ComputeResource';
import { Translate } from '@plesk/ui-library';
import { useRequestCancellationEffect } from 'common/hooks/useRequestCancellationEffect';
import { track } from 'common/analytics/mixpanel';
import { ANALYTICS_EVENTS } from 'common/analytics/constants';
import useQuery from 'common/hooks/useQuery';

export type TasksProps =
    RouteComponentProps &
    ReturnType<typeof mapDispatchToProps>;

export const Tasks: React.FC<TasksProps> = ({
    taskActions: {
        getTaskList,
    },
}) => {

    React.useEffect(() => {
        track(ANALYTICS_EVENTS.ADMIN.TASKS.VISIT);
    }, []);

    const [filters, setFilters] = useQuery<IFilterOptions>();

    const [selectedComputeResource, setSelectedComputeResource] = React.useState<ISelectOption>();

    const isFirstLoading = useRequestCancellationEffect(
        token => {
            getTaskList({ filters }, token);
        },
        [filters]
    );

    React.useEffect(() => {
        const fetchComputeResource = async () => {
            if (filters.compute_resource_id) {
                const computeResource = await computeResources.item(
                    Number(filters.compute_resource_id)
                );

                setSelectedComputeResource({
                    value: computeResource.data.data.id.toString(),
                    label: computeResource.data.data.host,
                });
            }
        };

        fetchComputeResource();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStatusFilterChange = async (option: PropsValue<ISelectOption>) => {
        setFilters(values => ({
            ...values,
            status: option ? (option as ISelectOption).value : '',
        }));
    };

    const handleActionFilterChange = async (option: PropsValue<ISelectOption>) => {
        setFilters(values => ({
            ...values,
            action: option ? (option as ISelectOption).value : '',
        }));
    };

    const handleComputeResourceFilterChange = async (option: PropsValue<ISelectOption>) => {
        const computeResourceOption = option as ISelectOption;

        setFilters(values => ({
            ...values,
            compute_resource_id: computeResourceOption ? parseFloat(computeResourceOption.value!) : undefined,
        }));

        setSelectedComputeResource(computeResourceOption);
    };

    return (
        <>
            <PageHeader title={<Translate content="task.title" />} isButtonShown={false} />
            <TaskTable
                isFirstLoading={isFirstLoading}
                filters={filters}
                toolbar={(
                    <Filters
                        data={filters}
                        selectedComputeResource={selectedComputeResource}
                        onActionFilterChange={handleActionFilterChange}
                        onStatusFilterChange={handleStatusFilterChange}
                        onComputeResourceFilterChange={handleComputeResourceFilterChange}
                    />
                )}
            />
        </>
    );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    taskActions: bindActionCreators(taskActions, dispatch),
});

export default connect(null, mapDispatchToProps)(Tasks);
